import { useState, useMemo, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Play,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  Copy,
  Check,
  Send,
  History,
  Trash2,
  ChevronRight,
  Zap,
  Server,
  FileJson,
  Code2,
  RotateCcw,
  Download,
  ArrowRight,
} from 'lucide-react';
import {
  TritonInstanceDetails,
  TritonModelInfo,
  mockTritonInstanceDetails,
} from '@/mock/gpu-instance';
import {
  useTritonModelStore,
  uploadedModelToTritonModelInfo,
} from '@/store/tritonModelStore';
import {
  getDefaultInputJson,
  generateMockResponse,
} from '../../components/model-management/schemas';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';

interface RequestHistoryItem {
  id: string;
  timestamp: Date;
  modelName: string;
  method: 'POST';
  endpoint: string;
  status: 'success' | 'error';
  latencyMs: number;
  request: string;
  response: string;
}

const GPUInstanceModelTesting = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const [searchParams] = useSearchParams();
  const preselectedModel = searchParams.get('model');

  // State
  const [selectedModel, setSelectedModel] = useState<string>(preselectedModel || '');
  const [requestBody, setRequestBody] = useState<string>('');
  const [isRunning, setIsRunning] = useState(false);
  const [response, setResponse] = useState<{
    status: 'success' | 'error' | null;
    statusCode: number;
    body: string;
    latencyMs: number;
    timestamp: Date | null;
  }>({
    status: null,
    statusCode: 0,
    body: '',
    latencyMs: 0,
    timestamp: null,
  });
  const [requestHistory, setRequestHistory] = useState<RequestHistoryItem[]>([]);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'body' | 'headers'>('body');
  const [responseTab, setResponseTab] = useState<'body' | 'headers'>('body');

  // Get store state
  const getAdditionalModels = useTritonModelStore((state) => state.getAdditionalModels);
  const isModelDeleted = useTritonModelStore((state) => state.isModelDeleted);

  const baseDetails: TritonInstanceDetails = useMemo(
    () => mockTritonInstanceDetails(instanceId || ''),
    [instanceId]
  );

  // Combine mock models with uploaded models, filter deleted ones
  const allModels: TritonModelInfo[] = useMemo(() => {
    const additionalModels = getAdditionalModels(instanceId || '');
    const uploadedModelInfos = additionalModels.map((m) =>
      uploadedModelToTritonModelInfo(m, instanceId || '')
    );
    const filteredBaseModels = baseDetails.models.filter(
      (m) => !isModelDeleted(instanceId || '', m.name)
    );
    return [...filteredBaseModels, ...uploadedModelInfos];
  }, [baseDetails.models, instanceId, getAdditionalModels, isModelDeleted]);

  const readyModels = allModels.filter((m) => m.status === 'ready');
  const selectedModelInfo = allModels.find((m) => m.name === selectedModel);

  // Update request body when model changes
  useEffect(() => {
    if (selectedModel && selectedModelInfo) {
      const defaultInput = getDefaultInputJson(selectedModel, selectedModelInfo.backend);
      setRequestBody(defaultInput);
    }
  }, [selectedModel, selectedModelInfo]);

  // Set preselected model
  useEffect(() => {
    if (preselectedModel && readyModels.some((m) => m.name === preselectedModel)) {
      setSelectedModel(preselectedModel);
    }
  }, [preselectedModel, readyModels]);

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSendRequest = async () => {
    if (!selectedModel || !selectedModelInfo) return;

    // Validate JSON
    try {
      JSON.parse(requestBody);
    } catch {
      setResponse({
        status: 'error',
        statusCode: 400,
        body: JSON.stringify({ error: 'Invalid JSON in request body' }, null, 2),
        latencyMs: 0,
        timestamp: new Date(),
      });
      return;
    }

    setIsRunning(true);
    setResponse({ status: null, statusCode: 0, body: '', latencyMs: 0, timestamp: null });

    // Simulate inference
    const mockResult = generateMockResponse(selectedModel, selectedModelInfo.backend);
    await new Promise((resolve) => setTimeout(resolve, mockResult.latencyMs + 200));

    const responseBody = JSON.stringify(mockResult.output, null, 2);
    const newResponse = {
      status: 'success' as const,
      statusCode: 200,
      body: responseBody,
      latencyMs: mockResult.latencyMs,
      timestamp: new Date(),
    };

    setResponse(newResponse);
    setIsRunning(false);

    // Add to history
    const historyItem: RequestHistoryItem = {
      id: `req-${Date.now()}`,
      timestamp: new Date(),
      modelName: selectedModel,
      method: 'POST',
      endpoint: selectedModelInfo.endpoint,
      status: 'success',
      latencyMs: mockResult.latencyMs,
      request: requestBody,
      response: responseBody,
    };
    setRequestHistory((prev) => [historyItem, ...prev].slice(0, 20));
  };

  const handleLoadFromHistory = (item: RequestHistoryItem) => {
    setSelectedModel(item.modelName);
    setRequestBody(item.request);
    setResponse({
      status: item.status,
      statusCode: item.status === 'success' ? 200 : 500,
      body: item.response,
      latencyMs: item.latencyMs,
      timestamp: item.timestamp,
    });
  };

  const handleClearHistory = () => {
    setRequestHistory([]);
  };

  const handleFormatJson = () => {
    try {
      const parsed = JSON.parse(requestBody);
      setRequestBody(JSON.stringify(parsed, null, 2));
    } catch {
      // Invalid JSON, do nothing
    }
  };

  const handleDownloadResponse = () => {
    if (!response.body) return;
    const blob = new Blob([response.body], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `response-${selectedModel}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="container py-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl shadow-lg">
            <Zap className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Model Testing</h1>
            <p className="text-muted-foreground">
              Test inference requests with a Postman-like interface
            </p>
          </div>
        </div>
        <Badge variant="outline" className="gap-1.5 px-3 py-1">
          <Server className="h-3.5 w-3.5" />
          {readyModels.length} Models Ready
        </Badge>
      </div>

      {/* URL Bar */}
      <Card className="border-2">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Badge className="bg-green-600 hover:bg-green-600 text-white font-mono text-sm px-3 py-1">
              POST
            </Badge>
            <div className="flex-1">
              <Select value={selectedModel} onValueChange={setSelectedModel}>
                <SelectTrigger className="font-mono text-sm h-10 bg-muted/30">
                  <SelectValue placeholder="Select a model to test..." />
                </SelectTrigger>
                <SelectContent>
                  {readyModels.length === 0 ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      No ready models available
                    </div>
                  ) : (
                    readyModels.map((model) => (
                      <SelectItem key={model.name} value={model.name}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{model.name}</span>
                          <Badge variant="outline" className="text-[10px] font-mono">
                            {model.backend}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleSendRequest}
              disabled={!selectedModel || isRunning}
              size="lg"
              className="gap-2 px-6 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
            >
              {isRunning ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Send
            </Button>
          </div>

          {selectedModelInfo && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t">
              <span className="text-xs text-muted-foreground">Endpoint:</span>
              <code className="flex-1 px-3 py-1.5 bg-muted/50 rounded text-xs font-mono truncate">
                {selectedModelInfo.endpoint}
              </code>
              <Button
                variant="ghost"
                size="sm"
                className="h-7"
                onClick={() => handleCopy(selectedModelInfo.endpoint, 'endpoint')}
              >
                {copiedField === 'endpoint' ? (
                  <Check className="h-3.5 w-3.5 text-green-500" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Main Content */}
      <div className="grid grid-cols-12 gap-4">
        {/* History Sidebar */}
        <div className="col-span-2">
          <Card className="h-[600px] flex flex-col">
            <CardHeader className="py-3 px-4 border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <History className="h-4 w-4" />
                  History
                </CardTitle>
                {requestHistory.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={handleClearHistory}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <ScrollArea className="flex-1">
              {requestHistory.length === 0 ? (
                <div className="p-4 text-center text-xs text-muted-foreground">
                  No requests yet
                </div>
              ) : (
                <div className="p-2 space-y-1">
                  {requestHistory.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleLoadFromHistory(item)}
                      className="w-full p-2 rounded-lg text-left hover:bg-muted/50 transition-colors group"
                    >
                      <div className="flex items-center gap-1.5">
                        <div
                          className={cn(
                            'w-2 h-2 rounded-full',
                            item.status === 'success' ? 'bg-green-500' : 'bg-red-500'
                          )}
                        />
                        <span className="text-xs font-medium truncate flex-1">
                          {item.modelName}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
                        <span>{item.latencyMs}ms</span>
                        <span>{item.timestamp.toLocaleTimeString()}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </Card>
        </div>

        {/* Request Panel */}
        <div className="col-span-5">
          <Card className="h-[600px] flex flex-col">
            <CardHeader className="py-3 px-4 border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <ArrowRight className="h-4 w-4 text-blue-500" />
                  Request
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs gap-1"
                  onClick={handleFormatJson}
                >
                  <RotateCcw className="h-3 w-3" />
                  Format
                </Button>
              </div>
            </CardHeader>
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'body' | 'headers')} className="flex-1 flex flex-col">
              <div className="px-4 border-b">
                <TabsList className="h-9 bg-transparent p-0">
                  <TabsTrigger value="body" className="text-xs gap-1.5 data-[state=active]:bg-muted rounded-b-none">
                    <FileJson className="h-3 w-3" />
                    Body
                  </TabsTrigger>
                  <TabsTrigger value="headers" className="text-xs gap-1.5 data-[state=active]:bg-muted rounded-b-none">
                    <Code2 className="h-3 w-3" />
                    Headers
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="body" className="flex-1 m-0">
                <Textarea
                  value={requestBody}
                  onChange={(e) => setRequestBody(e.target.value)}
                  className="h-full resize-none rounded-none border-0 font-mono text-xs bg-[#0d1117] text-[#e6edf3] focus-visible:ring-0 leading-relaxed"
                  placeholder='{"inputs": [...]}'
                />
              </TabsContent>

              <TabsContent value="headers" className="flex-1 m-0 p-4 overflow-auto bg-muted/20">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 p-2.5 bg-background rounded-lg border text-sm">
                    <span className="font-medium text-muted-foreground w-32">Content-Type</span>
                    <span className="font-mono text-xs">application/json</span>
                  </div>
                  <div className="flex items-center gap-2 p-2.5 bg-background rounded-lg border text-sm">
                    <span className="font-medium text-muted-foreground w-32">Authorization</span>
                    <span className="font-mono text-xs">Bearer sk-****</span>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </Card>
        </div>

        {/* Response Panel */}
        <div className="col-span-5">
          <Card className="h-[600px] flex flex-col">
            <CardHeader className="py-3 px-4 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <ArrowRight className="h-4 w-4 text-green-500 rotate-180" />
                    Response
                  </CardTitle>
                  {response.status && (
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className={cn(
                          'gap-1 text-xs',
                          response.status === 'success'
                            ? 'text-green-600 border-green-500/30 bg-green-500/10'
                            : 'text-red-600 border-red-500/30 bg-red-500/10'
                        )}
                      >
                        {response.status === 'success' ? (
                          <CheckCircle2 className="h-3 w-3" />
                        ) : (
                          <XCircle className="h-3 w-3" />
                        )}
                        {response.statusCode}
                      </Badge>
                      <Badge variant="outline" className="gap-1 text-xs">
                        <Clock className="h-3 w-3" />
                        {response.latencyMs}ms
                      </Badge>
                    </div>
                  )}
                </div>
                {response.body && (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7"
                      onClick={() => handleCopy(response.body, 'response')}
                    >
                      {copiedField === 'response' ? (
                        <Check className="h-3.5 w-3.5 text-green-500" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7"
                      onClick={handleDownloadResponse}
                    >
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <Tabs value={responseTab} onValueChange={(v) => setResponseTab(v as 'body' | 'headers')} className="flex-1 flex flex-col">
              <div className="px-4 border-b">
                <TabsList className="h-9 bg-transparent p-0">
                  <TabsTrigger value="body" className="text-xs gap-1.5 data-[state=active]:bg-muted rounded-b-none">
                    <FileJson className="h-3 w-3" />
                    Body
                  </TabsTrigger>
                  <TabsTrigger value="headers" className="text-xs gap-1.5 data-[state=active]:bg-muted rounded-b-none">
                    <Code2 className="h-3 w-3" />
                    Headers
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="body" className="flex-1 m-0">
                {response.body ? (
                  <pre className="h-full overflow-auto p-4 font-mono text-xs bg-[#0d1117] text-[#7ee787] leading-relaxed">
                    {response.body}
                  </pre>
                ) : (
                  <div className="h-full flex items-center justify-center bg-muted/20">
                    <div className="text-center">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted/50 flex items-center justify-center">
                        <Send className="h-8 w-8 text-muted-foreground/30" />
                      </div>
                      <p className="text-sm text-muted-foreground">Send a request to see the response</p>
                      <p className="text-xs text-muted-foreground/60 mt-1">
                        Select a model and click Send
                      </p>
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="headers" className="flex-1 m-0 p-4 overflow-auto bg-muted/20">
                {response.status ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 p-2.5 bg-background rounded-lg border text-sm">
                      <span className="font-medium text-muted-foreground w-36">Content-Type</span>
                      <span className="font-mono text-xs">application/json</span>
                    </div>
                    <div className="flex items-center gap-2 p-2.5 bg-background rounded-lg border text-sm">
                      <span className="font-medium text-muted-foreground w-36">X-Inference-Time</span>
                      <span className="font-mono text-xs">{response.latencyMs}ms</span>
                    </div>
                    <div className="flex items-center gap-2 p-2.5 bg-background rounded-lg border text-sm">
                      <span className="font-medium text-muted-foreground w-36">X-Model-Name</span>
                      <span className="font-mono text-xs">{selectedModel}</span>
                    </div>
                    <div className="flex items-center gap-2 p-2.5 bg-background rounded-lg border text-sm">
                      <span className="font-medium text-muted-foreground w-36">X-Model-Version</span>
                      <span className="font-mono text-xs">1</span>
                    </div>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <p className="text-sm text-muted-foreground">No response headers yet</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>

            {/* Response Timestamp */}
            {response.timestamp && (
              <div className="px-4 py-2 border-t text-xs text-muted-foreground bg-muted/20">
                Response received at {response.timestamp.toLocaleTimeString()}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default GPUInstanceModelTesting;
